import { useEffect, useState } from 'react'
import { api, errorMessage } from '../api.js'
import { Alert, Button, Field, Input } from '../components/ui.js'

/** Inscricao: mostra o QR, confirma o codigo, entrega os codigos de recuperacao. */
export function TotpEnroll({ onDone }: { onDone: () => void }) {
  const [qr, setQr] = useState<{ qrDataUrl: string; manualKey: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.auth.totpStart.mutate().then(setQr).catch((e) => setError(errorMessage(e)))
  }, [])

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const res = await api.auth.totpConfirm.mutate({ code })
      setCodes(res.recoveryCodes)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (codes) {
    return (
      <Centered title="Guarde os codigos de recuperacao">
        <p className="text-sm text-ink-2">
          Sao dez codigos de uso unico. Servem para entrar se voce perder o celular — e nao serao
          exibidos de novo. Copie para um lugar seguro e fora deste computador.
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2 rounded border border-line bg-ground p-4 font-mono text-sm">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="mt-5 flex gap-2">
          <Button onClick={() => void navigator.clipboard.writeText(codes.join('\n'))}>
            Copiar
          </Button>
          <Button variant="primary" onClick={onDone}>
            Guardei — entrar no painel
          </Button>
        </div>
      </Centered>
    )
  }

  return (
    <Centered title="Configure o segundo fator">
      <p className="text-sm text-ink-2">
        Escaneie o codigo com Google Authenticator, Authy, 1Password ou Bitwarden. Sem isso a
        sessao nao abre — este painel comanda a infraestrutura inteira.
      </p>

      {qr ? (
        <>
          <div className="mt-5 inline-block rounded border border-line bg-white p-3">
            <img src={qr.qrDataUrl} alt="QR Code do segundo fator" width={220} height={220} />
          </div>
          <p className="mt-3 font-mono text-xs text-ink-3">
            Sem camera? Digite a chave: <span className="text-ink">{qr.manualKey}</span>
          </p>
        </>
      ) : (
        <p className="mt-5 text-sm text-ink-3">Gerando o codigo...</p>
      )}

      <div className="mt-5 max-w-[220px]">
        <Field label="Codigo gerado pelo app">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            placeholder="000000"
            className="font-mono text-lg tracking-[0.3em]"
          />
        </Field>
      </div>

      {error && <div className="mt-4"><Alert>{error}</Alert></div>}

      <div className="mt-5">
        <Button variant="primary" onClick={confirm} disabled={code.length !== 6 || busy}>
          {busy ? 'Confirmando...' : 'Confirmar e ativar'}
        </Button>
      </div>
    </Centered>
  )
}

/** Segundo fator no login de quem ja tem TOTP. */
export function TotpVerify({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (recovery) await api.auth.recoveryVerify.mutate({ code: code.trim() })
      else await api.auth.totpVerify.mutate({ code })
      onDone()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Centered title="Segundo fator">
      <form onSubmit={submit}>
        <div className="max-w-[240px]">
          <Field label={recovery ? 'Codigo de recuperacao' : 'Codigo do autenticador'}>
            <Input
              value={code}
              onChange={(e) =>
                setCode(recovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              inputMode={recovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              placeholder={recovery ? 'XXXXX-XXXXX' : '000000'}
              className="font-mono text-lg tracking-[0.2em]"
              autoFocus
            />
          </Field>
        </div>

        {error && <div className="mt-4"><Alert>{error}</Alert></div>}

        <div className="mt-5 flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={busy || code.length < 6}>
            {busy ? 'Verificando...' : 'Entrar'}
          </Button>
          <button
            type="button"
            onClick={() => { setRecovery(!recovery); setCode(''); setError(null) }}
            className="text-xs text-ink-3 underline underline-offset-4 hover:text-accent"
          >
            {recovery ? 'Usar o app autenticador' : 'Perdi o celular — usar codigo de recuperacao'}
          </button>
        </div>
      </form>
    </Centered>
  )
}

function Centered({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="w-full max-w-xl rounded border border-line bg-panel p-8 shadow-sm">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">Via 360 Graus</p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight">{title}</h1>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}
